/**
 * The findings queue, Linear-style: one header (title, stage tabs, Filter with
 * the search inside, View), dense one-line rows grouped by document, a
 * right-click menu on every row, multi-select with a floating bulk bar.
 *
 * Every compliance gap is here: regulatory findings and the gap suggestions
 * (control amendments, missing documents). Record updates have their own page.
 *
 * Keys: j/k move · Enter open · x select · / or f filter · v view · Esc clear.
 *
 * @module components/regulation/finding/FindingsQueue
 */
import { Sieve } from "@lucasmarkes/hairline/react";
import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleSlashIcon,
  FileTextIcon,
  LinkIcon,
  ListFilterIcon,
  ScanSearchIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  UserRoundIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { HairlineArt } from "~/components/ui/hairline-art";
import { cn } from "~/lib/utils";
import type { OrganizationPerson } from "../../../state/governance";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Kbd } from "../../ui/kbd";
import { MenuItem, MenuPopup, MenuSeparator, MenuShortcut, MenuSub, MenuSubPopup, MenuSubTrigger } from "../../ui/menu";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../ui/select";
import { toastManager } from "../../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import { PageHeader, SegmentedTabs } from "../PageHeader";
import { RejectDialog } from "../policy/RejectDialog";
import { BASIS_LABEL, SEVERITY_LABEL, SEVERITY_ORDER, type FindingBasisKind, type FindingItem, type Severity } from "./findingItem";
import { BasisIcon, PersonFace, PropertyChip, SeverityGlyph, StateChip, StatusIcon, personLabel } from "./findingGlyphs";
import { assigneeOptions, NO_ASSIGNEE } from "./findingPickers";
import {
  activeFilterCount,
  documentKey,
  dueLabel,
  EMPTY_FINDING_QUEUE,
  FINDING_GROUPINGS,
  FINDING_ORDERINGS,
  FINDING_ROW_PROPERTIES,
  FINDING_STAGES,
  filterFindingItems,
  groupFindingItems,
  MISSING_DOCUMENTS_KEY,
  relativeAge,
  stageCounts,
  type FindingGrouping,
  type FindingOrdering,
  type FindingQueueView,
  type FindingRowProperty,
} from "./FindingsQueue.logic";
import { Picker } from "./Picker";
import type { FindingActions } from "./useFindingItems";

/** The queue view outlives the page, so Back from a finding finds it as it was. */
const savedViews = new Map<string, FindingQueueView>();

export function useFindingQueueView(organizationId: string): [FindingQueueView, (patch: Partial<FindingQueueView>) => void] {
  const [view, setView] = useState<FindingQueueView>(() => savedViews.get(organizationId) ?? EMPTY_FINDING_QUEUE);
  const update = (patch: Partial<FindingQueueView>) =>
    setView((previous) => {
      const next = { ...previous, ...patch };
      savedViews.set(organizationId, next);
      return next;
    });
  return [view, update];
}

/** The saved view for an organisation (the detail page's prev / next follow it). */
export const savedFindingQueueView = (organizationId: string) => savedViews.get(organizationId) ?? EMPTY_FINDING_QUEUE;

export const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable ||
    target.closest('[role="dialog"], [data-slot$="popup"], [role="menu"], [role="listbox"]') !== null);

/** j/k move between [data-row-link]s inside `root`. */
export function useRowKeys(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if ((event.key !== "j" && event.key !== "k") || isTyping(event.target)) return;
      const links = [...(root.current?.querySelectorAll<HTMLElement>("[data-row-link]") ?? [])];
      if (links.length === 0) return;
      event.preventDefault();
      const down = event.key === "j";
      const index = links.indexOf(document.activeElement as HTMLElement);
      const next = index === -1 ? (down ? 0 : links.length - 1) : Math.min(links.length - 1, Math.max(0, index + (down ? 1 : -1)));
      links[next]?.focus();
      links[next]?.scrollIntoView({ block: "nearest" });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [root]);
}

const toggle = <T,>(list: ReadonlyArray<T>, value: T, on: boolean): ReadonlyArray<T> =>
  on ? (list.includes(value) ? list : [...list, value]) : list.filter((entry) => entry !== value);

const findingHref = (orgSegment: string, id: string) => `/${orgSegment}/compliance/findings/${id}`;

export function copyFindingLink(orgSegment: string, id: string) {
  const link = `${window.location.origin}${findingHref(orgSegment, id)}`;
  void navigator.clipboard?.writeText(link).catch(() => {});
  toastManager.add({ type: "success", title: "Link copied", description: link.replace(/^https?:\/\//, "") });
}

export interface FindingRowContext {
  readonly orgSegment: string;
  readonly canManage: boolean;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly peopleById: ReadonlyMap<string, OrganizationPerson>;
  readonly selection: ReadonlyArray<string>;
  readonly onSelect: (ids: ReadonlyArray<string>) => void;
  readonly onAssign: (items: ReadonlyArray<FindingItem>, assigneeId: string | null) => void;
  readonly onDismiss: (items: ReadonlyArray<FindingItem>) => void;
  readonly itemsById: ReadonlyMap<string, FindingItem>;
  /** Rows on a document's page open the finding with a way back to it. */
  readonly fromPolicy?: string;
}

function RowContextMenu({ item, context, children }: { readonly item: FindingItem; readonly context: FindingRowContext; readonly children: ReactNode }) {
  const navigate = useNavigate();
  const selected = context.selection.includes(item.id);
  const targets =
    selected && context.selection.length > 1
      ? context.selection.map((id) => context.itemsById.get(id)).filter((candidate) => candidate !== undefined)
      : [item];
  return (
    <ContextMenuPrimitive.Root>
      <ContextMenuPrimitive.Trigger render={children as React.ReactElement} />
      <MenuPopup className="w-56" aria-label={`Actions for ${item.title}`}>
        <MenuItem onClick={() => void navigate({ to: "/$org/compliance/findings/$findingId", params: { org: context.orgSegment, findingId: item.id } })}>
          <ArrowUpRightIcon />
          Open
          <MenuShortcut>↵</MenuShortcut>
        </MenuItem>
        <MenuItem onClick={() => copyFindingLink(context.orgSegment, item.id)}>
          <LinkIcon />
          Copy link
        </MenuItem>
        <MenuItem onClick={() => context.onSelect(selected ? context.selection.filter((id) => id !== item.id) : [...context.selection, item.id])}>
          <CheckIcon />
          {selected ? "Deselect" : "Select"}
          <MenuShortcut>X</MenuShortcut>
        </MenuItem>
        {context.canManage ? (
          <>
            <MenuSeparator />
            <MenuSub>
              <MenuSubTrigger>
                <UserRoundIcon />
                Assign{targets.length > 1 ? ` ${targets.length}` : ""}
              </MenuSubTrigger>
              <MenuSubPopup className="w-56">
                {assigneeOptions(context.people, targets.length === 1 ? item.assigneeId : null).map((option) => (
                  <MenuItem key={option.value || "none"} onClick={() => context.onAssign(targets, option.value || null)}>
                    {option.icon}
                    {option.label}
                  </MenuItem>
                ))}
              </MenuSubPopup>
            </MenuSub>
            {targets.some((target) => target.status === "open") ? (
              <>
                <MenuSeparator />
                <MenuItem variant="destructive" onClick={() => context.onDismiss(targets.filter((target) => target.status === "open"))}>
                  <CircleSlashIcon />
                  Dismiss{targets.length > 1 ? ` ${targets.length}` : ""}…
                </MenuItem>
              </>
            ) : null}
          </>
        ) : null}
      </MenuPopup>
    </ContextMenuPrimitive.Root>
  );
}

export function FindingRow({
  item,
  context,
  showDocument,
  selectable = false,
  onSelectClick,
  properties = EMPTY_FINDING_QUEUE.properties,
}: {
  readonly item: FindingItem;
  readonly context: FindingRowContext;
  readonly showDocument: boolean;
  readonly selectable?: boolean;
  readonly onSelectClick?: (event: React.MouseEvent, item: FindingItem) => void;
  readonly properties?: ReadonlyArray<FindingRowProperty>;
}) {
  const selected = context.selection.includes(item.id);
  const show = (property: FindingRowProperty) => properties.includes(property);
  const assignee = item.assigneeId ? context.peopleById.get(item.assigneeId) : undefined;
  const due = item.dueDate && item.status === "open" ? dueLabel(item.dueDate) : null;
  // The width-capped chips sit above the stretched link to take the hover, so each is
  // its own link to the finding: modified and middle clicks behave like the title's.
  const chipLink = (chip: ReactNode, className: string) => (
    <Link
      to="/$org/compliance/findings/$findingId"
      params={{ org: context.orgSegment, findingId: item.id }}
      {...(context.fromPolicy ? { search: { fromPolicy: context.fromPolicy } } : {})}
      tabIndex={-1}
      aria-hidden
      className={cn("relative z-10", className)}
    >
      {chip}
    </Link>
  );
  return (
    <RowContextMenu item={item} context={context}>
      <div
        data-finding-row={item.id}
        data-selected={selected || undefined}
        className="group/row relative flex h-11 min-w-0 items-center gap-2.5 bg-card pe-4 ps-2 text-[13px] transition-colors duration-150 hover:bg-foreground/[0.035] has-[[data-row-link]:focus-visible]:bg-foreground/[0.05] data-selected:bg-primary/[0.06] sm:h-10 sm:pe-6 sm:ps-3 dark:data-selected:bg-primary/[0.12]"
      >
        {selectable ? (
          <span className="relative z-10 flex w-4 shrink-0 justify-center">
            <Checkbox
              aria-label={`Select ${item.title}`}
              checked={selected}
              onClick={(event) => onSelectClick?.(event, item)}
              className={cn(
                "size-3.5 transition-opacity duration-150 sm:size-3.5",
                selected || context.selection.length > 0 ? "opacity-100" : "opacity-0 focus-visible:opacity-100 group-hover/row:opacity-100 pointer-coarse:hidden",
              )}
            />
          </span>
        ) : (
          <span className="w-1" />
        )}
        <SeverityGlyph severity={item.severity} />
        <StatusIcon status={item.status} />
        <Link
          to="/$org/compliance/findings/$findingId"
          params={{ org: context.orgSegment, findingId: item.id }}
          {...(context.fromPolicy ? { search: { fromPolicy: context.fromPolicy } } : {})}
          data-row-link=""
          className="min-w-0 truncate font-medium text-foreground outline-none after:absolute after:inset-0"
          title={item.title}
        >
          {item.title}
        </Link>
        {show("state") && item.chip ? <StateChip chip={item.chip} className="relative shrink-0 max-sm:hidden" /> : null}
        <span className="ms-auto flex shrink-0 items-center gap-1.5 ps-2">
          {show("basis") ? chipLink(
            <PropertyChip icon={<BasisIcon kind={item.basis.kind} />} title={item.basis.label} className="max-w-36" expand>
              {item.basis.short}
            </PropertyChip>,
            "hidden lg:flex",
          ) : null}
          {show("document") && showDocument ? chipLink(
            <PropertyChip icon={<FileTextIcon />} title={item.documentName} className="max-w-44" expand>
              {item.documentName}
            </PropertyChip>,
            "hidden md:flex",
          ) : null}
          {show("due") && due ? (
            <PropertyChip icon={<CalendarIcon />} className={due.overdue ? "border-destructive/40 text-destructive" : undefined} title={`Due ${item.dueDate}`}>
              {due.text}
            </PropertyChip>
          ) : null}
          {show("assignee") && assignee ? (
            <span title={`Assigned to ${personLabel(assignee)}`}>
              <PersonFace person={assignee} />
              <span className="sr-only">Assigned to {personLabel(assignee)}</span>
            </span>
          ) : null}
          {show("age") ? (
            <time className="w-8 text-right text-xs tabular-nums text-muted-foreground" title={new Date(item.raisedAt).toLocaleString("en-GB")}>
              {relativeAge(item.raisedAt)}
            </time>
          ) : null}
        </span>
      </div>
    </RowContextMenu>
  );
}

function FilterPopover({
  open,
  onOpenChange,
  searchRef,
  view,
  setView,
  items,
  documents,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly searchRef: React.RefObject<HTMLInputElement | null>;
  readonly view: FindingQueueView;
  readonly setView: (patch: Partial<FindingQueueView>) => void;
  /** Rows of the current stage, for the counts beside each option. */
  readonly items: ReadonlyArray<FindingItem>;
  readonly documents: ReadonlyArray<readonly [string, string]>;
}) {
  const active = activeFilterCount(view);
  const section = <T extends string>(
    title: string,
    options: ReadonlyArray<T>,
    selected: ReadonlyArray<T>,
    label: (option: T) => string,
    icon: (option: T) => ReactNode,
    count: (option: T) => number,
    set: (next: ReadonlyArray<T>) => void,
  ) => (
    <fieldset className="min-w-0 border-t border-border/70 p-1.5 first:border-t-0">
      <legend className="px-2 pb-1 pt-1.5 text-[11px] font-medium text-muted-foreground">{title}</legend>
      {options.map((option) => (
        <label key={option} className="flex h-7 cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] transition-colors duration-100 hover:bg-foreground/[0.06]">
          <Checkbox className="size-3.5 sm:size-3.5" checked={selected.includes(option)} onCheckedChange={(checked) => set(toggle(selected, option, checked === true))} />
          {icon(option)}
          <span className="min-w-0 flex-1 truncate">{label(option)}</span>
          <span className="text-[11px] tabular-nums text-muted-foreground">{count(option)}</span>
        </label>
      ))}
    </fieldset>
  );
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger render={<Button variant="ghost" size="xs" aria-label={`Filter${active ? `, ${active} active` : ""}`} data-queue-filter="" />}>
              <ListFilterIcon aria-hidden />
              <span className="max-sm:hidden">Filter</span>
              {active > 0 ? (
                <Badge variant="secondary" size="sm" className="tabular-nums">
                  {active}
                </Badge>
              ) : null}
            </PopoverTrigger>
          }
        />
        <TooltipPopup>
          Filter and search <Kbd className="ms-1">F</Kbd> <Kbd>/</Kbd>
        </TooltipPopup>
      </Tooltip>
      <PopoverPopup align="end" className="w-72" viewportClassName="p-0 [--viewport-inline-padding:0px]" initialFocus={searchRef}>
        <label className="flex h-10 items-center gap-2 border-b border-border/70 px-3">
          <SearchIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={searchRef}
            data-queue-search=""
            value={view.query}
            onChange={(event) => setView({ query: event.currentTarget.value })}
            placeholder="Search findings…"
            aria-label="Search findings"
            className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
          />
          {view.query ? (
            <button type="button" aria-label="Clear search" onClick={() => setView({ query: "" })} className="cursor-pointer rounded-sm text-muted-foreground hover:text-foreground">
              <XIcon className="size-3.5" />
            </button>
          ) : null}
        </label>
        <div className="max-h-[min(28rem,60dvh)] overflow-y-auto">
          {section(
            "Severity",
            SEVERITY_ORDER,
            view.severities,
            (severity) => SEVERITY_LABEL[severity],
            (severity) => <SeverityGlyph severity={severity} />,
            (severity) => items.filter((item) => item.severity === severity).length,
            (severities) => setView({ severities }),
          )}
          {section(
            "Basis",
            ["regulation", "control", "missing"] as ReadonlyArray<FindingBasisKind>,
            view.bases,
            (basis) => BASIS_LABEL[basis],
            (basis) => <BasisIcon kind={basis} className="text-muted-foreground" />,
            (basis) => items.filter((item) => item.basis.kind === basis).length,
            (bases) => setView({ bases }),
          )}
          {section(
            "Document",
            documents.map(([key]) => key),
            view.documents,
            (key) => documents.find(([candidate]) => candidate === key)?.[1] ?? key,
            () => <FileTextIcon className="size-3.5 text-muted-foreground" />,
            (key) => items.filter((item) => documentKey(item) === key).length,
            (keys) => setView({ documents: keys }),
          )}
        </div>
        {active > 0 ? (
          <div className="border-t border-border/70 p-1.5">
            <Button variant="ghost" size="xs" className="w-full justify-start" onClick={() => setView({ severities: [], bases: [], documents: [], query: "" })}>
              <XIcon aria-hidden />
              Clear filters
            </Button>
          </div>
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}

function ViewPopover({
  open,
  onOpenChange,
  view,
  setView,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly view: FindingQueueView;
  readonly setView: (patch: Partial<FindingQueueView>) => void;
}) {
  const row = (label: string, control: ReactNode) => (
    <div className="flex h-9 items-center justify-between gap-3 px-3 text-[13px]">
      <span className="text-muted-foreground">{label}</span>
      {control}
    </div>
  );
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger render={<Button variant="ghost" size="xs" aria-label="Display options" data-queue-view="" />}>
              <SlidersHorizontalIcon aria-hidden />
              <span className="max-sm:hidden">View</span>
            </PopoverTrigger>
          }
        />
        <TooltipPopup>
          Display options <Kbd className="ms-1">V</Kbd>
        </TooltipPopup>
      </Tooltip>
      <PopoverPopup align="end" className="w-72" viewportClassName="p-0 [--viewport-inline-padding:0px]">
        <div className="py-1.5">
          {row(
            "Grouping",
            <Select value={view.grouping} onValueChange={(value) => setView({ grouping: (value ?? "document") as FindingGrouping, collapsed: [] })}>
              <SelectTrigger size="sm" className="w-36" aria-label="Grouping">
                <span>{FINDING_GROUPINGS.find(([key]) => key === view.grouping)?.[1]}</span>
              </SelectTrigger>
              <SelectContent>
                {FINDING_GROUPINGS.map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>,
          )}
          {row(
            "Ordering",
            <Select value={view.ordering} onValueChange={(value) => setView({ ordering: (value ?? "severity") as FindingOrdering })}>
              <SelectTrigger size="sm" className="w-36" aria-label="Ordering">
                <span>{FINDING_ORDERINGS.find(([key]) => key === view.ordering)?.[1]}</span>
              </SelectTrigger>
              <SelectContent>
                {FINDING_ORDERINGS.map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>,
          )}
        </div>
        <div className="border-t border-border/70 px-3 py-2.5">
          <p className="mb-2 text-[11px] font-medium text-muted-foreground">Display properties</p>
          <div className="flex flex-wrap gap-1">
            {FINDING_ROW_PROPERTIES.map(([key, label]) => {
              const on = view.properties.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setView({ properties: toggle(view.properties, key, !on) })}
                  className={cn(
                    "h-6 cursor-pointer rounded-md border px-2 text-[11px] outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring",
                    on ? "border-border bg-foreground/[0.07] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverPopup>
    </Popover>
  );
}

function BulkBar({ context }: { readonly context: FindingRowContext }) {
  const [assignOpen, setAssignOpen] = useState(false);
  const ids = context.selection;
  const items = ids.map((id) => context.itemsById.get(id)).filter((item) => item !== undefined);
  const openItems = items.filter((item) => item.status === "open");
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target) || ids.length === 0 || !context.canManage) return;
      if (event.key === "a") {
        event.preventDefault();
        setAssignOpen(true);
      } else if (event.key === "d" && openItems.length > 0) {
        event.preventDefault();
        context.onDismiss(openItems);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [ids, openItems, context]);
  if (ids.length === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label="Selected findings"
      data-bulk-bar=""
      className="fixed bottom-5 left-1/2 z-30 flex -translate-x-1/2 items-center gap-1 rounded-xl border border-border bg-popover px-1.5 py-1.5 text-[13px] shadow-lg/10 animate-in fade-in slide-in-from-bottom-2 duration-150"
    >
      <span className="flex items-center gap-1.5 rounded-md border border-dashed border-border px-2 py-1 text-xs tabular-nums">
        {ids.length} selected
        <button type="button" aria-label="Clear selection" onClick={() => context.onSelect([])} className="cursor-pointer text-muted-foreground hover:text-foreground">
          <XIcon className="size-3.5" />
        </button>
      </span>
      {context.canManage ? (
        <>
          <Picker
            label="Assign"
            shortcut="a"
            open={assignOpen}
            onOpenChange={setAssignOpen}
            align="center"
            options={assigneeOptions(context.people, null).map((option) => ({ ...option, checked: false }))}
            onPick={(value) => context.onAssign(items, value === NO_ASSIGNEE ? null : value)}
            trigger={
              <>
                <UserRoundIcon className="size-3.5 text-muted-foreground" />
                Assign
              </>
            }
          />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button size="xs" variant="ghost" disabled={openItems.length === 0} onClick={() => context.onDismiss(openItems)}>
                  <CircleSlashIcon aria-hidden />
                  Dismiss…
                </Button>
              }
            />
            <TooltipPopup>
              Dismiss <Kbd className="ms-1">D</Kbd>
            </TooltipPopup>
          </Tooltip>
        </>
      ) : (
        <span className="px-2 text-xs text-muted-foreground">Waiting for an admin to act on these</span>
      )}
    </div>
  );
}

function GroupIcon({ grouping, group, peopleById }: { readonly grouping: FindingGrouping; readonly group: { key: string }; readonly peopleById: ReadonlyMap<string, OrganizationPerson> }) {
  switch (grouping) {
    case "document":
      return <FileTextIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />;
    case "severity":
      return <SeverityGlyph severity={group.key === "unrated" ? null : (group.key as Severity)} />;
    case "basis":
      return <BasisIcon kind={group.key as FindingBasisKind} className="text-muted-foreground" />;
    case "assignee": {
      const person = peopleById.get(group.key);
      return person ? <PersonFace person={person} className="size-4 text-[8px]" /> : <span className="size-4 rounded-full border border-dashed border-border" />;
    }
    case "none":
      return null;
  }
}

/** Dismiss one or several findings with one reason (bulk). */
export function useDismissFlow(actions: FindingActions) {
  const [targets, setTargets] = useState<ReadonlyArray<FindingItem>>([]);
  const dialog = (
    <RejectDialog
      open={targets.length > 0}
      onOpenChange={(open) => {
        if (!open) setTargets([]);
      }}
      what="finding"
      dismiss
      allowProof={false}
      title={targets.length === 1 ? targets[0]!.title : targets.length > 1 ? `${targets.length} findings` : null}
      onReject={async ({ reason, category }) => {
        if (!category) return "Choose why it is dismissed.";
        const total = targets.length;
        for (const [index, target] of targets.entries()) {
          const error = await actions.dismiss(target, { category, note: reason });
          if (error !== null) {
            if (total === 1) return error;
            // The ones before this are dismissed: keep only the rest, so a retry resumes here.
            setTargets(targets.slice(index));
            return `${index} of ${total} dismissed. ${target.title}: ${error}`;
          }
        }
        toastManager.add({
          type: "success",
          title: targets.length === 1 ? "Finding dismissed" : `${targets.length} findings dismissed`,
          description: "The reason is kept in its history. Reopen it to undo.",
        });
        return null;
      }}
    />
  );
  return { open: setTargets, dialog };
}

export function FindingsQueuePage({
  organizationId,
  orgSegment,
  items,
  people,
  peopleById,
  personName,
  canManage,
  actions,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly items: ReadonlyArray<FindingItem>;
  readonly people: ReadonlyArray<OrganizationPerson>;
  readonly peopleById: ReadonlyMap<string, OrganizationPerson>;
  readonly personName: (id: string) => string | null;
  readonly canManage: boolean;
  readonly actions: FindingActions;
}) {
  const [view, setView] = useFindingQueueView(organizationId);
  const [selection, setSelection] = useState<ReadonlyArray<string>>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const anchor = useRef<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const dismissFlow = useDismissFlow(actions);
  useRowKeys(listRef);

  const filtered = useMemo(() => filterFindingItems(items, view, personName), [items, view, personName]);
  const counts = useMemo(() => stageCounts(filtered), [filtered]);
  const groups = useMemo(() => groupFindingItems(filtered, view, personName), [filtered, view, personName]);
  const inStage = useMemo(() => items.filter((item) => view.stage === "all" || item.status === view.stage), [items, view.stage]);
  const documents = useMemo(() => {
    const seen = new Map<string, string>();
    for (const item of items) seen.set(documentKey(item), documentKey(item) === MISSING_DOCUMENTS_KEY ? "Missing documents" : item.documentName);
    return [...seen.entries()].sort((left, right) => left[1].localeCompare(right[1]));
  }, [items]);
  const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);
  const filteredCount = activeFilterCount(view);

  const assign = (targets: ReadonlyArray<FindingItem>, assigneeId: string | null) => {
    void Promise.all(targets.map((target) => actions.plan(target, { assigneeId }))).then((errors) => {
      const error = errors.find((candidate) => candidate !== null);
      const name = assigneeId ? (personName(assigneeId) ?? "them") : null;
      if (error) toastManager.add({ type: "error", title: "Could not assign", description: error });
      else
        toastManager.add({
          type: "success",
          title:
            targets.length === 1
              ? name
                ? `Assigned to ${name}`
                : "Unassigned"
              : `${targets.length} findings ${name ? `assigned to ${name}` : "unassigned"}`,
        });
    });
  };

  const context: FindingRowContext = {
    orgSegment,
    canManage,
    people,
    peopleById,
    selection,
    onSelect: setSelection,
    onAssign: assign,
    onDismiss: (targets) => {
      if (targets.length > 0) dismissFlow.open(targets);
    },
    itemsById,
  };

  const selectClick = (event: React.MouseEvent, item: FindingItem) => {
    const ids = groups.flatMap((group) => (view.collapsed.includes(group.key) ? [] : group.items.map((candidate) => candidate.id)));
    if (event.shiftKey && anchor.current && ids.includes(anchor.current)) {
      const [from = 0, to = 0] = [ids.indexOf(anchor.current), ids.indexOf(item.id)].sort((left, right) => left - right);
      setSelection([...new Set([...selection, ...ids.slice(from, to + 1)])]);
    } else {
      setSelection(selection.includes(item.id) ? selection.filter((id) => id !== item.id) : [...selection, item.id]);
    }
    anchor.current = item.id;
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (event.key === "/" || event.key === "f") {
        event.preventDefault();
        setViewOpen(false);
        setFilterOpen(true);
      } else if (event.key === "v") {
        event.preventDefault();
        setFilterOpen(false);
        setViewOpen(true);
      } else if (event.key === "x") {
        const id = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>("[data-finding-row]")?.dataset.findingRow ?? null;
        if (!id) return;
        event.preventDefault();
        setSelection((current) => (current.includes(id) ? current.filter((other) => other !== id) : [...current, id]));
        anchor.current = id;
      } else if (event.key === "Escape" && selection.length > 0) {
        event.preventDefault();
        setSelection([]);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selection.length]);

  const rows = (groupItems: ReadonlyArray<FindingItem>) =>
    groupItems.map((item) => (
      <FindingRow
        key={item.id}
        item={item}
        context={context}
        showDocument={view.grouping !== "document"}
        selectable
        onSelectClick={selectClick}
        properties={view.properties}
      />
    ));
  const visibleCount = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <div data-regulation-page="true" data-findings-queue="" className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Findings"
        tabs={
          <SegmentedTabs
            label="Finding status"
            value={view.stage}
            onValueChange={(stage) => {
              setView({ stage });
              setSelection([]);
            }}
            tabs={FINDING_STAGES.map(([stage, label]) => ({ value: stage, label, count: counts[stage] }))}
          />
        }
        actions={
          <>
            {view.query.trim() ? (
              <Button size="xs" variant="secondary" className="max-w-40" onClick={() => setFilterOpen(true)} aria-label={`Search: ${view.query}`}>
                <SearchIcon aria-hidden />
                <span className="truncate">{view.query}</span>
              </Button>
            ) : null}
            <FilterPopover open={filterOpen} onOpenChange={setFilterOpen} searchRef={searchRef} view={view} setView={setView} items={inStage} documents={documents} />
            <ViewPopover open={viewOpen} onOpenChange={setViewOpen} view={view} setView={setView} />
          </>
        }
      />
      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto pb-24" aria-label="Findings queue">
        {visibleCount === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-20 text-center">
            {items.length === 0 ? (
              <HairlineArt figure={Sieve} className="mb-2 w-48 sm:w-56" />
            ) : (
              <ScanSearchIcon aria-hidden className="size-6 text-muted-foreground/50" />
            )}
            <p className="text-[13px] text-muted-foreground">
              {items.length === 0 ? "No findings yet. Monitoring and Inspector raise them against your documents." : filteredCount > 0 ? "No findings match" : "No findings here"}
            </p>
            {filteredCount > 0 ? (
              <Button size="xs" variant="outline" onClick={() => setView({ query: "", severities: [], bases: [], documents: [] })}>
                Clear filters
              </Button>
            ) : null}
          </div>
        ) : view.grouping === "none" ? (
          <div className="divide-y divide-border/50">{rows(groups[0]?.items ?? [])}</div>
        ) : (
          groups.map((group) => {
            const collapsed = view.collapsed.includes(group.key);
            return (
              <section key={group.key} aria-label={group.label}>
                <div className="group/header sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-border/60 bg-muted/60 pe-4 ps-2.5 backdrop-blur sm:pe-6 sm:ps-3.5 dark:bg-muted/40">
                  <button
                    type="button"
                    aria-expanded={!collapsed}
                    onClick={() => setView({ collapsed: collapsed ? view.collapsed.filter((key) => key !== group.key) : [...view.collapsed, group.key] })}
                    className="flex min-w-0 cursor-pointer items-center gap-2 rounded-md py-1 pe-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <ChevronDownIcon aria-hidden className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform duration-150", collapsed && "-rotate-90")} />
                    <GroupIcon grouping={view.grouping} group={group} peopleById={peopleById} />
                    <span className="truncate font-medium">{group.label}</span>
                    <span className="text-xs tabular-nums text-muted-foreground">{group.items.length}</span>
                  </button>
                  {view.grouping === "document" && group.key !== MISSING_DOCUMENTS_KEY ? (
                    <Link
                      to="/$org/compliance/policies/$policyId"
                      params={{ org: orgSegment, policyId: group.key }}
                      hash="findings"
                      className="ms-auto flex items-center gap-0.5 text-xs text-muted-foreground opacity-0 transition-opacity duration-150 hover:text-foreground focus-visible:opacity-100 group-hover/header:opacity-100 pointer-coarse:opacity-100"
                    >
                      Open document
                      <ChevronRightIcon aria-hidden className="size-3" />
                    </Link>
                  ) : null}
                </div>
                {collapsed ? null : <div className="divide-y divide-border/50">{rows(group.items)}</div>}
              </section>
            );
          })
        )}
      </div>
      <BulkBar context={context} />
      {dismissFlow.dialog}
    </div>
  );
}
